import * as React from 'react';
import { Dispatch, SetStateAction } from 'react';
import { ButtonV2, Text } from '@asigna/kit';
import BigNumber from 'bignumber.js';
import cx from 'classnames';
import { NoData, SkeletonAsset, TokenIcon } from 'components';
import { usePermissionWarning, useReadOnlyWarning } from 'hooks';
import { SafeStxBalance, useTokenPrices } from 'store';
import { AssetListFromBalancesType } from 'utils/asset-list';

import { formatAssetBalance } from '../../../../utils/formatting';

type Props = {
  assetList: AssetListFromBalancesType[];
  stxBalance: SafeStxBalance;
  setClickIndex: Dispatch<SetStateAction<number | null>>;
  showInstantSendDialog: () => void;
  loading: boolean;
};

// TODO_R: [FRS]
export const AssetsTab = ({
  assetList,
  setClickIndex,
  showInstantSendDialog,
  loading,
  stxBalance,
}: Props) => {
  const { tokenPrices } = useTokenPrices();

  const stacking = new BigNumber(stxBalance.locked);
  const stackingBalance = stacking.toString();
  const readOnlyWarning = useReadOnlyWarning();
  const permissionCreateWarning = usePermissionWarning('PROPOSE');

  return (
    <>
      {loading ? (
        <SkeletonAsset mode={0} />
      ) : assetList.length === 0 ? (
        <NoData isAssets />
      ) : (
        <div className="px-0 sm:px-0">
          <div
            className={cx(
              'hidden sm:grid',
              stacking.isGreaterThan(0) ? 'grid-cols-5' : 'grid-cols-4'
            )}
          >
            <Text message="Token" color="regular" />
            <Text message="Price" color="regular" />
            <Text message="Balance" color="regular" />
            {stacking.isGreaterThan(0) && <Text message="" color="regular" />}
            <Text message="" color="regular" className="text-right" />
          </div>
          <div className="flex flex-col w-full gap-12">
            {assetList
              .filter((asset) => Number(asset.balance) > 0)
              .map((asset, index) => {
                const price = tokenPrices[asset.address];
                const usdBalance = price && (Number(asset.balance) * price).toFixed(2);
                const stackingBalanceUsd = stacking.isGreaterThan(0)
                  ? stacking.toNumber() * price
                  : 0;

                let priceShow = price ? `~$${price.toFixed(2)}` : '-';
                if (price > 0.000001 && price < 1) priceShow = `~$${price.toFixed(5)}`;
                else if (price > 0 && price < 0.000001) priceShow = '< $0.000001';

                return (
                  <div
                    className={cx(
                      'flex flex-wrap justify-between sm:grid items-center py-16 border-black-4 border-b-[.5px] gap-8 sm:gap-0',
                      stacking.isGreaterThan(0) ? 'grid-cols-5' : 'grid-cols-4'
                    )}
                    key={`asset_${asset.symbol + asset.tokenName + asset.address}`}
                  >
                    <div className="flex items-center gap-8 sm:gap-16">
                      <TokenIcon tokenIcon={asset.tokenIcon} chainIcon="" />
                      <div>
                        <Text message={asset.symbol} color="white" size="m14" />
                        <Text
                          message={priceShow}
                          color="white"
                          size="r12"
                          className="flex sm:hidden"
                        />
                      </div>
                    </div>
                    <Text message={priceShow} color="white" size="m14" className="hidden sm:flex" />
                    {usdBalance ? (
                      <div className="flex flex-col items-end sm:items-start flex-1">
                        <Text
                          message={formatAssetBalance(+asset.balance)}
                          color="white"
                          size="m14"
                        />
                        <Text message={`$${usdBalance}`} color="regular" size="m14" />
                      </div>
                    ) : (
                      <div className="flex flex-col items-end sm:items-start flex-1">
                        <Text
                          message={formatAssetBalance(+asset.balance)}
                          color="white"
                          size="m14"
                        />
                      </div>
                    )}
                    {stacking.isGreaterThan(0) && (
                      <>
                        {asset.symbol === 'STX' ? (
                          <div className="flex flex-col">
                            <div className="flex items-center gap-4">
                              <span className="text-m14">{stackingBalance}</span>
                              <span className="text-r13 text-regular">~ ${stackingBalanceUsd}</span>
                            </div>
                            <span className="text-r13 text-regular">Stacking balance</span>
                          </div>
                        ) : (
                          <div />
                        )}
                      </>
                    )}
                    <ButtonV2
                      onClick={(e) => {
                        e.stopPropagation();
                        setClickIndex(index);
                        showInstantSendDialog();
                      }}
                      size="sm"
                      disabled={Boolean(readOnlyWarning) || Boolean(permissionCreateWarning)}
                      disableHint={readOnlyWarning || permissionCreateWarning}
                      hintPosition="bottom-end"
                      className="sm:min-w-92 justify-self-center sm:justify-self-end"
                    >
                      Send
                    </ButtonV2>
                  </div>
                );
              })}
          </div>
        </div>
      )}
    </>
  );
};
